import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LuLayoutGrid } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { getHexagonFeatures } from '@utils/api';
import {
	makeHexagonLayer,
	getHexagonOpacity,
	hexagonStyle,
	makeHexagonFeatures,
} from '@utils/map/layer/hexagon';
import { addHexagonInteraction } from '../sidebars/HexagonInfo';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';
import { HexagonSummaryPreview } from '../sidebars/summary/HexagonSummarySidebar';
import {
	ToggleSlideButton,
	ToggleSlideContent,
} from '@components/toggleSlideContent/ToggleSlideContent';
import { getLayerByCustomId } from '@utils/map/helpers';
import FilterHexagon from '../modifyLayers/FilterHexagon';

const layerId = 'hexagonLayer';
const HexagonLayer = ({
	setModifyHexagonMode,
	setHexagonColorFilterActive,
	modifyHexagonsSidebarId,
}) => {
	const { mapObject, hexagonLayer, project, pickedTask, hexagonSize, hexagonColor, dispatch } =
		useProject();

	const taskId = pickedTask?.model_uuid;

	const [showLayerDetails, setShowLayerDetails] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ['fetchHexagons', taskId, project.uuid, hexagonSize],
		queryFn: () => getHexagonFeatures(project.uuid, taskId, hexagonSize),
		enabled: !!project?.uuid && !!taskId && !!mapObject,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const hexagonInteractionAdded = useRef(false);

	const { hexagons } = useMemo(() => {
		if (!data?.hexagon_data) {
			return { hexagons: [] };
		}

		let hexagons = []; // Initialize hexagons as an empty array

		if (!!data?.hexagon_data) {
			let maxCount = 0;

			Object.keys(data.hexagon_data).forEach(key => {
				const hexagon = { ...data.hexagon_data[key] };
				hexagon.id = key;

				if (hexagon.metadata.count > maxCount) {
					maxCount = hexagon.metadata.count;
				}

				hexagons.push(hexagon);
			});

			hexagons.map(hexagon => {
				hexagon.metadata.heatIndicator = (hexagon.metadata.count / maxCount).toFixed(2);
				Object.keys(hexagon.detections).forEach(key => {
					hexagon.detections[key].name = key;
				});

				return hexagon;
			});
		}

		return { hexagons };
	}, [data]);

	const updateHexagonLayer = useCallback(() => {
		if (isLoading) return;

		if (hexagons && hexagons.length > 0) {
			let layer;
			const existingLayer = getLayerByCustomId(mapObject, layerId);
			if (existingLayer) {
				layer = existingLayer;
				const layerSource = layer.getSource();
				layerSource.clear();
				const newFeatures = makeHexagonFeatures(hexagons, hexagonColor);
				layerSource.addFeatures(newFeatures);
			} else {
				layer = makeHexagonLayer({
					currentMap: mapObject,
					features: hexagons,
					hexagonColor,
					customLayerId: layerId,
				});

				addHexagonInteraction(layer, mapObject, dispatch, hexagonInteractionAdded);
			}

			dispatch({ type: 'setHexagonLayer', payload: layer });
			dispatch({ type: 'setHexagonInfo', payload: data });
		} else {
			if (mapObject && hexagonLayer) {
				mapObject.removeLayer(hexagonLayer);

				hexagonLayer.dispose();

				if (hexagonInteractionAdded.current) {
					mapObject.removeInteraction(hexagonInteractionAdded.current);
					hexagonInteractionAdded.current = false;
				}

				dispatch({ type: 'setHexagonLayer', payload: null });
				dispatch({ type: 'setHexagonInfo', payload: null });
				dispatch({ type: 'setSelectedHexagon', payload: null });
			}
		}
	}, [hexagons]);

	useEffect(() => {
		updateHexagonLayer();
	}, [updateHexagonLayer]);

	useEffect(() => {
		hexagonLayer
			?.getSource()
			?.getFeatures()
			?.forEach(feature => {
				const heatIndicator = feature.getProperties()?.data?.metadata?.heatIndicator;
				const hexagonOpacity = getHexagonOpacity(heatIndicator);
				const style = hexagonStyle(hexagonColor.color, hexagonOpacity);
				feature.setStyle(style);
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hexagonColor?.color]);

	if (!hexagonLayer) return null;

	return (
		<li>
			<div className="d-flex flex-row justify-content-between align-items-center">
				<LayerSettings
					id="hexagon-layer-settings"
					layer={hexagonLayer}
					label={hexagonLayer.get('name')}
					defaultState={hexagonLayer?.getVisible()}
					onCheck={() => {
						hexagonLayer?.setVisible(true);
					}}
					onUncheck={() => {
						hexagonLayer?.setVisible(false);
					}}
					layerFunctions={[
						{
							title: 'Modify',
							onClick: () => {
								setModifyHexagonMode(true);
							},
						},
						{
							title: 'Color Filter',
							icon: <LuLayoutGrid />,
							onClick: setHexagonColorFilterActive,
						},
					]}
				/>
				<div className="position-relative me-2">
					<div className="position-absolute top-0 start-100 translate-middle">
						<ToggleSlideButton
							constrolsId="hexagon-layer-details"
							isOpen={showLayerDetails}
							setIsOpen={setShowLayerDetails}
						/>
					</div>
				</div>
			</div>

			<ToggleSlideContent isOpen={showLayerDetails} id="hexagon-layer-details">
				<HexagonSummaryPreview />
			</ToggleSlideContent>

			<FilterHexagon sidebarId={modifyHexagonsSidebarId} />
		</li>
	);
};

export default HexagonLayer;
